/**
 * The Frayer model: a four-square vocabulary organizer.
 *
 * The term goes in the middle; the four squares ask for a definition, the term's
 * characteristics, examples and non-examples. This module only prepares the text: the
 * list of terms (one organizer per term) and the optional model entry for the first.
 */

export const MAX_TERMS = 12;
export const MAX_TERM_LENGTH = 60;
/** Lines of a model entry kept per square. */
export const MAX_MODEL_LINES = 8;

const LINE_BREAK = /\r?\n/;
const SPACES = /\s+/g;

export type Square =
  | "definition"
  | "characteristics"
  | "examples"
  | "nonExamples";
export const SQUARES: readonly Square[] = [
  "definition",
  "characteristics",
  "examples",
  "nonExamples",
];

export type Model = Record<Square, string>;

export const EMPTY_MODEL: Model = {
  definition: "",
  characteristics: "",
  examples: "",
  nonExamples: "",
};

export interface ParsedTerms {
  terms: string[];
  /** Repeated terms, kept once. */
  duplicates: number;
  /** Terms beyond `MAX_TERMS`, left out. */
  overflow: number;
  /** Terms shortened to `MAX_TERM_LENGTH` characters. */
  clipped: number;
}

/** One term per line, spaces tidied, repeats (ignoring case) kept once. */
export function parseTerms(text: string): ParsedTerms {
  const terms: string[] = [];
  const seen = new Set<string>();
  let duplicates = 0;
  let overflow = 0;
  let clipped = 0;
  for (const raw of text.split(LINE_BREAK)) {
    const tidy = raw.replace(SPACES, " ").trim();
    if (tidy === "") {
      continue;
    }
    const key = tidy.toLocaleLowerCase();
    if (seen.has(key)) {
      duplicates += 1;
    } else if (terms.length >= MAX_TERMS) {
      overflow += 1;
    } else {
      seen.add(key);
      if (tidy.length > MAX_TERM_LENGTH) {
        clipped += 1;
      }
      terms.push(tidy.slice(0, MAX_TERM_LENGTH).trim());
    }
  }
  return { terms, duplicates, overflow, clipped };
}

/** The non-blank lines of a model entry, at most `MAX_MODEL_LINES`. */
export function modelLines(text: string): string[] {
  return text
    .split(LINE_BREAK)
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .slice(0, MAX_MODEL_LINES);
}

/** Whether any square of the model has text. */
export function hasModel(model: Model): boolean {
  return SQUARES.some((square) => modelLines(model[square]).length > 0);
}
